import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import { Check, X } from "lucide-react";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchEmsBilling, createEmsBillingOrder, verifyEmsBillingPayment, downloadEmsReceipt, loadRazorpayScript } from "../../Apis/EmsBilling";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import { getDefaultHomePath } from "../../Utils/defaultHomePath";
import "./EmsPlansPage.css";

function formatInr(amount) {
  if (amount === null || amount === undefined) return "—";
  return `₹${Number(amount).toLocaleString("en-IN", {
    minimumFractionDigits: Number(amount) % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

function groupPaidPlans(plans, cycle) {
  const paid = (plans || []).filter(
    (p) => !p.is_trial && p.billing_cycle === cycle
  );
  const enterprise = (plans || []).find((p) => p.contact_sales);
  return { paid, enterprise };
}

/** Match plan tier without substring bugs (plan_iii must not match plan_ii). */
function planTierKey(plan) {
  const group = String(plan.display_group || "").toLowerCase();
  if (["plan_i", "plan_ii", "plan_iii", "plan_iv"].includes(group)) {
    return group;
  }
  const code = String(plan.plan_code || "").toLowerCase();
  if (code.includes("plan_iv")) return "plan_iv";
  if (code.includes("plan_iii")) return "plan_iii";
  if (code.includes("plan_ii")) return "plan_ii";
  if (code.includes("plan_i")) return "plan_i";
  const seats = Number(plan.default_license_count) || 0;
  if (seats === 1000) return "plan_iv";
  if (seats === 500) return "plan_iii";
  if (seats === 300) return "plan_ii";
  if (seats === 100) return "plan_i";
  return null;
}

function planFeatures(plan) {
  if (plan.contact_sales) {
    return [
      "Custom User Limit",
      "All IMS Modules",
      "Free Product Updates",
      "Dedicated Support",
      "Custom Deployment & Integrations",
    ];
  }

  const tier = planTierKey(plan);
  const seatLabel =
    tier === "plan_i"
      ? "Up to 100 Active Users"
      : tier === "plan_ii"
        ? "Up to 300 Active Users"
        : tier === "plan_iii"
          ? "Up to 500 Active Users"
          : tier === "plan_iv"
            ? "Up to 1,000 Active Users"
            : plan.default_license_count
              ? `Up to ${Number(plan.default_license_count).toLocaleString("en-IN")} Active Users`
              : "Custom User Limit";

  const support =
    tier === "plan_iv"
      ? "Premium Support"
      : tier === "plan_ii" || tier === "plan_iii"
        ? "Priority Support"
        : "Standard Support";

  return [
    seatLabel,
    "All IMS Modules",
    "Free Product Updates",
    support,
  ];
}

export default function EmsPlansPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userData, login } = useAuthStore();
  const forcedUpgrade =
    searchParams.get("upgrade") === "1" ||
    Boolean(userData?.billing_only) ||
    Boolean(localStorage.getItem("ems_billing_only"));

  const [loading, setLoading] = useState(true);
  const [upgradingId, setUpgradingId] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchEmsBilling();
      setSubscription(data.subscription || null);
      setPlans(data.plans || []);
      if (data.subscription?.billing_cycle === "yearly") {
        setBillingCycle("yearly");
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Failed to load plans."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const { paid, enterprise } = useMemo(
    () => groupPaidPlans(plans, billingCycle),
    [plans, billingCycle]
  );

  const highlightIndex = Math.min(1, Math.max(0, paid.length - 1));

  const closePlans = () => {
    if (forcedUpgrade) return;
    if (window.history.length > 1) navigate(-1);
    else navigate(getDefaultHomePath(userData), { replace: true });
  };

  const handleUpgrade = async (plan) => {
    if (!plan?.plan_id || plan.contact_sales) return;
    setUpgradingId(plan.plan_id);
    try {
      const scriptOk = await loadRazorpayScript();
      if (!scriptOk || !window.Razorpay) {
        toast.error("Unable to load Razorpay. Check your internet connection.");
        return;
      }

      const order = await createEmsBillingOrder(plan.plan_id);
      if (!order?.order_id || !order?.key_id) {
        toast.error(order?.error || "Could not create payment order.");
        return;
      }

      await new Promise((resolve, reject) => {
        const rzp = new window.Razorpay({
          key: order.key_id,
          amount: order.amount,
          currency: order.currency || "INR",
          name: "InsSure",
          description: `${plan.plan_name} (${plan.billing_cycle || "plan"})${
            order.tax?.payable
              ? ` · Sub ₹${Number(order.tax.subtotal).toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                })} + GST`
              : ""
          }`,
          order_id: order.order_id,
          prefill: {
            name: order.prefill?.name || userData?.full_name || "",
            email: order.prefill?.email || userData?.email || "",
            contact: order.prefill?.contact || "",
          },
          notes: {
            plan_id: String(plan.plan_id),
            plan_code: plan.plan_code || "",
          },
          theme: { color: "#0891B2" },
          handler: async (response) => {
            try {
              const result = await verifyEmsBillingPayment({
                plan_id: Number(plan.plan_id),
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              toast.success(result.message || "Payment successful. Plan upgraded.");
              localStorage.removeItem("ems_billing_only");
              if (userData) {
                login({
                  ...userData,
                  billing_only: false,
                  upgrade_required: false,
                  ems_subscription: result.subscription,
                });
              }
              setSubscription(result.subscription || null);
              if (result.receipt?.client_payment_id) {
                try {
                  await downloadEmsReceipt(
                    result.receipt.client_payment_id,
                    result.receipt.invoice_number ||
                      result.receipt.receipt_number,
                    "invoice"
                  );
                } catch {
                  /* documents still available later on Billing page */
                }
              }
              // Stay on Billing (plan + receipts). Do not send to EMS dashboard.
              navigate("/billing", { replace: true });
              resolve(result);
            } catch (err) {
              toast.error(
                err.response?.data?.error ||
                  err.response?.data?.message ||
                  "Payment verification failed."
              );
              reject(err);
            }
          },
          modal: {
            ondismiss: () => {
              toast("Payment cancelled.");
              resolve(null);
            },
          },
        });

        rzp.on("payment.failed", (response) => {
          toast.error(
            response?.error?.description ||
              response?.error?.reason ||
              "Payment failed. Please try again."
          );
          resolve(null);
        });

        rzp.open();
      });
    } catch (err) {
      const data = err.response?.data;
      const msg =
        data?.details ||
        data?.error ||
        data?.message ||
        "Unable to start payment.";
      toast.error(msg);
    } finally {
      setUpgradingId(null);
    }
  };

  const cards = [
    ...paid.map((plan, idx) => ({ type: "paid", plan, featured: idx === highlightIndex })),
    ...(enterprise ? [{ type: "enterprise", plan: enterprise, featured: false }] : []),
  ];

  return (
    <div className="ems-glass-overlay" role="dialog" aria-modal="true" aria-labelledby="ems-plans-heading">
      <div className="ems-glass-backdrop" onClick={closePlans} />
      <div className="ems-glass-stage">
        <div className="ems-glass-glow" aria-hidden="true" />

        {!forcedUpgrade && (
          <button type="button" className="ems-glass-close" onClick={closePlans} aria-label="Close plans">
            <X size={18} />
          </button>
        )}

        <div className="ems-glass-panel">
          <div className="ems-glass-header">
            <span className="ems-glass-pill">Pricing Table</span>
            <h1 id="ems-plans-heading" className="ems-glass-title">
              Affordable plans with no hidden fees.
            </h1>
            <p className="ems-glass-subtitle">
              GST Included · Upgrade or downgrade your plan anytime.
            </p>

            {subscription && (
              <div className="ems-glass-current">
                <span>
                  Current:{" "}
                  <strong>
                    {subscription.plan_name ||
                      subscription.subscription_plan ||
                      "No plan"}
                  </strong>
                </span>
                <span className={`ems-glass-status ${subscription.is_subscription_active ? "ok" : "warn"}`}>
                  {subscription.is_subscription_active
                    ? subscription.is_trial
                      ? "Trial active"
                      : "Active"
                    : "Upgrade required"}
                </span>
              </div>
            )}

            {forcedUpgrade && (
              <div className="ems-glass-alert warn">
                Your trial or subscription has ended. Choose a plan to continue.
              </div>
            )}
            {error && <div className="ems-glass-alert error">{error}</div>}

            <div className="ems-glass-toggle" role="group" aria-label="Billing cycle">
              <button
                type="button"
                className={billingCycle === "monthly" ? "active" : ""}
                onClick={() => setBillingCycle("monthly")}
              >
                Monthly
              </button>
              <button
                type="button"
                className={billingCycle === "yearly" ? "active" : ""}
                onClick={() => setBillingCycle("yearly")}
              >
                Yearly <em>(Save 10%)</em>
              </button>
            </div>
          </div>

          {loading ? (
            <div className="ems-glass-loading">
              <CircularProgress size={28} sx={{ color: "var(--ems-primary)" }} />
              <p>Loading plans…</p>
            </div>
          ) : (
            <div className="ems-glass-grid">
              {cards.map(({ type, plan, featured }) => {
                const isEnterprise = type === "enterprise";
                const isCurrent =
                  !isEnterprise &&
                  Number(subscription?.plan_id) === Number(plan.plan_id);
                const displayPrice =
                  !isEnterprise &&
                  billingCycle === "yearly" &&
                  plan.monthly_equivalent
                    ? plan.monthly_equivalent
                    : plan.price; // Discounted Total (GST included) — never Sub Total
                const cancelledAmount =
                  !isEnterprise
                    ? Number(plan.list_price ?? plan.features?.list_price ?? 0)
                    : 0;
                const features = planFeatures(plan);

                return (
                  <article
                    key={plan.plan_id}
                    className={`ems-glass-card${featured ? " featured" : ""}${isEnterprise ? " enterprise" : ""}`}
                  >
                    <div className="ems-glass-card-top">
                      <h2>{plan.plan_name}</h2>
                      <div className="ems-glass-price-row">
                        {isEnterprise ? (
                          <span className="ems-glass-price">Custom</span>
                        ) : (
                          <>
                            {cancelledAmount > 0 ? (
                              <span className="ems-glass-price-cancel">
                                {formatInr(cancelledAmount)}
                              </span>
                            ) : null}
                            <span className="ems-glass-price">
                              {formatInr(displayPrice)}
                            </span>
                            <span className="ems-glass-price-meta">
                              {billingCycle === "yearly"
                                ? "Per month, billed yearly · GST Included"
                                : "Per month, GST Included"}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="ems-glass-includes">
                      <p>Includes</p>
                      <ul>
                        {features.map((item) => (
                          <li key={item}>
                            <Check size={14} strokeWidth={2.5} />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {isEnterprise ? (
                      <button type="button" className="ems-glass-cta ghost" disabled>
                        Contact sales
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={`ems-glass-cta ${featured ? "primary" : "ghost"}`}
                        disabled={isCurrent || upgradingId === plan.plan_id}
                        onClick={() => handleUpgrade(plan)}
                      >
                        {upgradingId === plan.plan_id ? (
                          <CircularProgress size={16} sx={{ color: "inherit" }} />
                        ) : isCurrent ? (
                          "Current plan"
                        ) : forcedUpgrade || subscription?.upgrade_required ? (
                          "Upgrade & pay"
                        ) : (
                          "Get started"
                        )}
                      </button>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
